import React from "react";
import {
  Box,
  Typography,
  Collapse,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import theme from "../../utils/theme";
import IconButtons from "../IconButtons";
import PaginationComponent from "../PaginationComponent";

// Table configuration constants
const FABRICATION_COLUMNS = [
  { header: "Model Name", key: "model_name", align: "left" },
  { header: "Model ID", key: "model_id", align: "left" },
  { header: "Created Date", key: "created_at", align: "left" },
  {
    header: "Components",
    key: "no_of_components",
    align: "left",
    customStyle: { pl: 3 },
  },
  { header: "Actions", key: "actions", align: "left", isAction: true },
];

const PAINTING_COLUMNS = [
  { header: "Model Name", key: "model_name", align: "left" },
  { header: "Model ID", key: "model_id", align: "left" },
  { header: "Created Date", key: "created_at", align: "left" },
  {
    header: "Parts",
    key: "no_of_items",
    align: "left",
    customStyle: { pl: 3 },
  },
  { header: "Actions", key: "actions", align: "left", isAction: true },
];

const styles = {
  container: {
    position: "relative",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // mb: 2,
    bgcolor: theme.palette.background.dark,
    borderRadius: "6px 6px 0 0",
    p: 1.5,
    color: theme.palette.text.light,
  },
  collapseContainer: {
    bgcolor: theme.palette.background.paper,
    p: 2,
    borderRadius: "0 0 6px 6px",
    mt: 0,
  },
  actionContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
    gap: 2,
  },
  toggleButton: {
    textTransform: "none",
    color: theme.palette.text.light,
  },
  table: {
    minWidth: 650,
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& th": {
      fontWeight: "bold",
    },
  },
  tableRowHead: {
    backgroundColor: theme.palette.grey[200],
  },
  tableCellHead: { color: theme.palette.text.primary },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.background.default,
    },
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.grey[100],
    },
    "&:hover": {
      backgroundColor: theme.palette.grey[200],
      cursor: "pointer",
    },
  },
  tableCell: {
    py: 1.5,
    textAlign: "left",
  },
  searchField: {
    flexGrow: 1,
    maxWidth: 400,
  },
  addButton: {
    textTransform: "none",
  },
  componentChip: {
    marginRight: 1,
    marginBottom: 1,
  },
  pagination: {
    position: "sticky",
    bottom: 0,
    backgroundColor: "background.paper",
    py: 1,
    display: "flex",
    justifyContent: "center",
    borderTop: "1px solid",
    borderColor: "divider",
  },
};

const TemplateList = ({
  onTemplateClick,
  createTemplateClick,
  allTemplates,
  collapsed = false,
  setCollapsed,
  onTemplateDelete,
  setSearchTerm,
  pagination,
  handlePageChange,
  loading,
  templateType = "fabrication", // Default value is fabrication
}) => {
  // Select appropriate columns based on template type
  const TABLE_COLUMNS =
    templateType === "painting" ? PAINTING_COLUMNS : FABRICATION_COLUMNS;

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Typography variant="h4" component="h1">
          Templates
        </Typography>
        <Button
          onClick={toggleCollapse}
          endIcon={
            collapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />
          }
          sx={styles.toggleButton}
        >
          {collapsed ? "Show" : "Hide"}
        </Button>
      </Box>

      <Collapse
        in={!collapsed}
        timeout="auto"
        unmountOnExit
        sx={styles.collapseContainer}
      >
        <Box sx={styles.actionContainer}>
          <TextField
            placeholder="Search templates..."
            variant="outlined"
            size="small"
            sx={styles.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={handleSearchChange}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={createTemplateClick}
            sx={styles.addButton}
          >
            Add New Template
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={styles.table} aria-label="templates table">
            <TableHead sx={styles.tableHeader}>
              <TableRow sx={styles.tableRowHead}>
                {TABLE_COLUMNS.map((column, index) => (
                  <TableCell
                    key={index}
                    sx={styles.tableCellHead}
                    align={column.align}
                  >
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {!loading ? (
              <TableBody>
                {allTemplates?.map((template, index) => (
                  <TableRow
                    key={index}
                    sx={styles.tableRow}
                    onClick={() => onTemplateClick(template)}
                  >
                    {TABLE_COLUMNS.map((column, colIndex) => (
                      <TableCell
                        key={colIndex}
                        sx={{
                          ...styles.tableCell,
                          ...column.customStyle,
                          ...(column.isAction && {
                            display: "flex",
                            gap: 1,
                            zIndex: 2,
                          }),
                        }}
                      >
                        {column.isAction ? (
                          <IconButtons
                            buttonType="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              onTemplateDelete(template);
                            }}
                          />
                        ) : column.key === "created_at" ? (
                          new Date(template[column.key]).toLocaleDateString()
                        ) : (
                          template[column.key]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={60} />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
        {pagination.total > pagination.perPage && (
          <PaginationComponent
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        )}
      </Collapse>
    </Box>
  );
};

export default TemplateList;
